import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTable } from "@/components/ui/data-table";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RoleService } from "@/gen/omnios/v1/rbac_connect.js";
import type {
	PermissionEntry,
	RoleDefinition,
} from "@/gen/omnios/v1/rbac_pb.js";
import { transport } from "@/lib/grpc";
import { getResourceLabels, getScopeGroups } from "@/lib/scopes";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useSlashFocus } from "@/lib/use-slash-focus";
import { cn } from "@/lib/utils";
import { createClient } from "@connectrpc/connect";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Check, Plus, Search, Shield, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

function scopeKey(resource: string, action: string) {
	return `${resource}:${action}`;
}

export const Route = createFileRoute("/settings/roles")({
	component: RolesSettingsPage,
});

const client = createClient(RoleService, transport);

interface RoleRow {
	id: string;
	name: string;
	displayName: string;
	description: string;
	isSystem: boolean;
	permissionCount: number;
	permissions: { resource: string; action: string }[];
}

function toRow(r: RoleDefinition): RoleRow {
	return {
		id: r.id,
		name: r.name,
		displayName: r.displayName,
		description: r.description,
		isSystem: r.isSystem,
		permissionCount: r.permissions.length,
		permissions: r.permissions.map((p) => ({
			resource: p.resource,
			action: p.action,
		})),
	};
}

function RolesSettingsPage() {
	const { t } = useTranslation("settings");

	useBreadcrumbs([
		{
			label: t("breadcrumb_settings"),
			href: "/settings",
		},
		{ label: t("roles_breadcrumb") },
	]);

	const [roles, setRoles] = useState<RoleRow[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [search, setSearch] = useState("");
	const searchRef = useRef<HTMLInputElement>(null);
	const clearSearch = useCallback(() => setSearch(""), []);
	useSlashFocus(searchRef, clearSearch);
	const [createOpen, setCreateOpen] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState<RoleRow | null>(null);
	const [detailTarget, setDetailTarget] = useState<RoleRow | null>(null);

	const filteredRoles = roles;

	const fetchRoles = useCallback(async (q?: string) => {
		setIsLoading(true);
		setError(null);
		try {
			const resp = await client.listRoles({
				search: q ?? "",
			});
			setRoles(resp.roles.map(toRow));
		} catch (e: unknown) {
			setError(e instanceof Error ? e.message : t("roles_load_error"));
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchRoles(search.trim());
	}, [fetchRoles, search]);

	const handleDelete = async (id: string) => {
		try {
			await client.deleteRole({ id });
			setRoles((prev) => prev.filter((r) => r.id !== id));
			setDeleteTarget(null);
		} catch {
			setDeleteTarget(null);
		}
	};

	const columns: ColumnDef<RoleRow, unknown>[] = [
		{
			accessorKey: "displayName",
			header: t("roles_col_role"),
			cell: ({ row }) => (
				<div className="flex items-center gap-2">
					<Shield className="h-4 w-4 text-muted-foreground" />
					<div>
						<p className="text-sm font-medium">
							{row.original.displayName || row.original.name}
						</p>
						{row.original.description && (
							<p className="text-xs text-muted-foreground">
								{row.original.description}
							</p>
						)}
					</div>
				</div>
			),
		},
		{
			accessorKey: "name",
			header: t("roles_col_identifier"),
			cell: ({ row }) => (
				<code className="text-xs text-muted-foreground">
					{row.original.name}
				</code>
			),
		},
		{
			accessorKey: "permissionCount",
			header: t("roles_col_permissions"),
			cell: ({ row }) => (
				<Badge variant="secondary">{row.original.permissionCount}</Badge>
			),
		},
		{
			id: "type",
			header: t("roles_col_type"),
			cell: ({ row }) =>
				row.original.isSystem ? (
					<Badge variant="outline">{t("roles_type_system")}</Badge>
				) : (
					<Badge variant="secondary">{t("roles_type_custom")}</Badge>
				),
		},
		{
			id: "actions",
			header: "",
			cell: ({ row }) => {
				if (row.original.isSystem) return null;
				return (
					<Button
						variant="ghost"
						size="icon"
						className="text-muted-foreground hover:text-destructive"
						onClick={(e) => {
							e.stopPropagation();
							setDeleteTarget(row.original);
						}}
					>
						<Trash2 className="h-4 w-4" />
					</Button>
				);
			},
		},
	];

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between gap-4">
				<div className="relative">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						ref={searchRef}
						placeholder={t("roles_search_placeholder")}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="pl-9 h-9 w-56"
					/>
				</div>
				<Button size="sm" onClick={() => setCreateOpen(true)}>
					<Plus className="h-4 w-4" />
					{t("roles_new_role")}
				</Button>
			</div>

			{error && (
				<div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
					{error}
				</div>
			)}

			{isLoading ? (
				<div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
					{t("common:loading")}
				</div>
			) : (
				<DataTable
					columns={columns}
					data={filteredRoles}
					onRowClick={(row) => setDetailTarget(row)}
				/>
			)}

			<CreateRoleDialog
				open={createOpen}
				onOpenChange={setCreateOpen}
				onCreated={(role) => {
					setRoles((prev) => [...prev, role]);
					setCreateOpen(false);
				}}
			/>

			<RoleDetailDialog
				role={detailTarget}
				onClose={() => setDetailTarget(null)}
			/>

			<AlertDialog
				open={!!deleteTarget}
				onOpenChange={(open) => {
					if (!open) setDeleteTarget(null);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("roles_delete_title")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("roles_delete_desc", {
								name: deleteTarget?.displayName || deleteTarget?.name,
							})}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								if (deleteTarget) {
									handleDelete(deleteTarget.id);
								}
							}}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{t("common:delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}

function allReadKeys(): Set<string> {
	const s = new Set<string>();
	for (const g of getScopeGroups()) {
		const readAction = g.actions.find((a) => a.action === "read");
		if (readAction) {
			s.add(scopeKey(g.resource, "read"));
		}
	}
	return s;
}

function allKeys(): Set<string> {
	const s = new Set<string>();
	for (const g of getScopeGroups()) {
		for (const a of g.actions) {
			s.add(scopeKey(g.resource, a.action));
		}
	}
	return s;
}

function PermissionPicker({
	selected,
	onChange,
	disabled,
}: {
	selected: Set<string>;
	onChange: (s: Set<string>) => void;
	disabled?: boolean;
}) {
	const { t } = useTranslation("settings");
	const [filter, setFilter] = useState("");

	const toggle = (key: string) => {
		const next = new Set(selected);
		if (next.has(key)) next.delete(key);
		else next.add(key);
		onChange(next);
	};

	const toggleResource = (resource: string, actions: string[]) => {
		const keys = actions.map((a) => scopeKey(resource, a));
		const allOn = keys.every((k) => selected.has(k));
		const next = new Set(selected);
		for (const k of keys) {
			if (allOn) next.delete(k);
			else next.add(k);
		}
		onChange(next);
	};

	const q = filter.toLowerCase().trim();
	const visibleGroups = q
		? getScopeGroups().filter(
				(g) =>
					g.label.toLowerCase().includes(q) ||
					g.resource.toLowerCase().includes(q),
			)
		: getScopeGroups();

	const totalAll = allKeys().size;

	return (
		<div className="space-y-2">
			<div className="flex items-center gap-2">
				<div className="relative flex-1">
					<Search className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
					<Input
						placeholder={t("roles_filter_placeholder")}
						value={filter}
						onChange={(e) => setFilter(e.target.value)}
						className="h-8 pl-7 text-xs"
						disabled={disabled}
					/>
				</div>
				<div className="flex items-center gap-1 shrink-0">
					<Button
						type="button"
						variant="ghost"
						size="sm"
						className="h-7 text-xs px-2"
						disabled={disabled}
						onClick={() => onChange(allReadKeys())}
					>
						{t("roles_read_only")}
					</Button>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						className="h-7 text-xs px-2"
						disabled={disabled}
						onClick={() => onChange(allKeys())}
					>
						{t("common:all")}
					</Button>
					{selected.size > 0 && (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="h-7 text-xs px-2 text-muted-foreground"
							disabled={disabled}
							onClick={() => onChange(new Set())}
						>
							{t("roles_reset")}
						</Button>
					)}
				</div>
			</div>

			<div className="text-[11px] text-muted-foreground tabular-nums">
				{t("roles_selected_count", {
					selected: selected.size,
					total: totalAll,
				})}
			</div>

			<div className="space-y-1">
				{visibleGroups.length === 0 && (
					<p className="text-xs text-muted-foreground py-4 text-center">
						{t("roles_nothing_found")}
					</p>
				)}
				{visibleGroups.map((group) => {
					const keys = group.actions.map((a) =>
						scopeKey(group.resource, a.action),
					);
					const checkedCount = keys.filter((k) => selected.has(k)).length;
					const allChecked = checkedCount === keys.length;
					const someChecked = checkedCount > 0 && !allChecked;

					return (
						<div
							key={group.resource}
							className={cn(
								"rounded-lg border px-3 py-2 transition-colors",
								checkedCount > 0 && "border-primary/30 bg-primary/[0.02]",
							)}
						>
							<div className="flex items-center gap-2">
								<Checkbox
									checked={
										allChecked ? true : someChecked ? "indeterminate" : false
									}
									onCheckedChange={() =>
										toggleResource(
											group.resource,
											group.actions.map((a) => a.action),
										)
									}
									disabled={disabled}
								/>
								<span className="text-sm font-medium flex-1">
									{group.label}
								</span>
								{checkedCount > 0 && (
									<span className="text-[10px] tabular-nums text-primary">
										{checkedCount}/{keys.length}
									</span>
								)}
								<code className="text-[10px] text-muted-foreground">
									{group.resource}
								</code>
							</div>
							<div className="flex flex-wrap gap-x-4 gap-y-1 ml-6 mt-1.5">
								{group.actions.map((a) => {
									const key = scopeKey(group.resource, a.action);
									return (
										<label
											key={key}
											className={cn(
												"flex items-center gap-1.5 text-xs cursor-pointer select-none",
												disabled && "opacity-50 cursor-not-allowed",
											)}
										>
											<Checkbox
												checked={selected.has(key)}
												onCheckedChange={() => toggle(key)}
												disabled={disabled}
												className="h-3.5 w-3.5"
											/>
											{a.label}
										</label>
									);
								})}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}

function setToPermissions(
	s: Set<string>,
): { resource: string; action: string }[] {
	return Array.from(s).map((key) => {
		const [resource, action] = key.split(":");
		return { resource, action };
	});
}

function slugify(s: string): string {
	return s
		.toLowerCase()
		.replace(/[а-яё]/g, (c) => {
			const map: Record<string, string> = {
				а: "a",
				б: "b",
				в: "v",
				г: "g",
				д: "d",
				е: "e",
				ё: "yo",
				ж: "zh",
				з: "z",
				и: "i",
				й: "y",
				к: "k",
				л: "l",
				м: "m",
				н: "n",
				о: "o",
				п: "p",
				р: "r",
				с: "s",
				т: "t",
				у: "u",
				ф: "f",
				х: "kh",
				ц: "ts",
				ч: "ch",
				ш: "sh",
				щ: "shch",
				ъ: "",
				ы: "y",
				ь: "",
				э: "e",
				ю: "yu",
				я: "ya",
			};
			return map[c] ?? c;
		})
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_|_$/g, "");
}

function CreateRoleDialog({
	open,
	onOpenChange,
	onCreated,
}: {
	open: boolean;
	onOpenChange: (v: boolean) => void;
	onCreated: (role: RoleRow) => void;
}) {
	const [name, setName] = useState("");
	const [nameManual, setNameManual] = useState(false);
	const [displayName, setDisplayName] = useState("");
	const [description, setDescription] = useState("");
	const [saving, setSaving] = useState(false);
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [error, setError] = useState<string | null>(null);
	const { t } = useTranslation("settings");

	const effectiveName = nameManual ? name : slugify(displayName);

	const reset = () => {
		setName("");
		setNameManual(false);
		setDisplayName("");
		setDescription("");
		setSelected(new Set());
		setError(null);
	};

	const handleSubmit = async () => {
		if (!effectiveName.trim()) return;
		setSaving(true);
		setError(null);
		try {
			const permissions = setToPermissions(
				selected,
			) as unknown as PermissionEntry[];

			const resp = await client.createRole({
				name: effectiveName.trim(),
				displayName: displayName.trim() || effectiveName.trim(),
				description: description.trim(),
				permissions,
			});
			onCreated(toRow(resp));
			reset();
		} catch (e) {
			setError(e instanceof Error ? e.message : t("roles_create_error"));
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(v) => {
				if (!v) reset();
				onOpenChange(v);
			}}
		>
			<DialogContent className="max-w-lg max-h-[90vh] flex flex-col gap-0 p-0">
				<DialogHeader className="px-6 pt-6 pb-2">
					<DialogTitle>{t("roles_create_title")}</DialogTitle>
					<DialogDescription>{t("roles_create_desc")}</DialogDescription>
				</DialogHeader>
				<div className="space-y-4 px-6 pb-2 overflow-y-auto flex-1 min-h-0">
					<div className="space-y-1.5">
						<Label>
							{t("roles_create_name_label")}
						</Label>
						<Input
							placeholder={t("roles_create_name_placeholder")}
							value={displayName}
							onChange={(e) => setDisplayName(e.target.value)}
							autoFocus
						/>
					</div>
					<div className="space-y-1.5">
						<div className="flex items-center justify-between">
							<Label>
								{t("roles_create_id_label")}
							</Label>
							{!nameManual && effectiveName && (
								<span className="text-[10px] text-muted-foreground">
									{t("roles_create_id_auto")}
								</span>
							)}
						</div>
						<Input
							placeholder="catalog_editor"
							value={nameManual ? name : effectiveName}
							onChange={(e) => {
								setNameManual(true);
								setName(e.target.value);
							}}
							className="font-mono text-xs"
						/>
					</div>
					<div className="space-y-1.5">
						<Label>
							{t("roles_create_desc_label")}
							<span className="text-muted-foreground font-normal ml-1">
								{t("roles_create_desc_optional")}
							</span>
						</Label>
						<Input
							placeholder={t("roles_create_desc_placeholder")}
							value={description}
							onChange={(e) => setDescription(e.target.value)}
						/>
					</div>
					<div className="space-y-2">
						<Label>
							{t("roles_create_permissions_label")}
						</Label>
						<PermissionPicker selected={selected} onChange={setSelected} />
					</div>
				</div>
				{error && <p className="text-sm text-destructive px-6">{error}</p>}
				<DialogFooter className="px-6 pb-6 pt-4">
					<Button
						variant="outline"
						onClick={() => {
							reset();
							onOpenChange(false);
						}}
					>
						{t("common:cancel")}
					</Button>
					<Button
						onClick={handleSubmit}
						disabled={!effectiveName.trim() || !displayName.trim() || saving}
					>
						{saving ? t("roles_creating") : t("roles_create_submit")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function PermissionGrid({
	permissions,
}: {
	permissions: {
		resource: string;
		action: string;
	}[];
}) {
	const { t } = useTranslation("settings");

	const ACTION_LABELS: Record<string, string> = {
		read: t("roles_action_read"),
		create: t("roles_action_create"),
		update: t("roles_action_update"),
		delete: t("roles_action_delete"),
		execute: t("roles_action_execute"),
		"*": t("roles_action_all"),
	};

	const isWildcard = permissions.some(
		(p) => p.resource === "*" && p.action === "*",
	);

	if (isWildcard) {
		return (
			<div className="rounded-lg border bg-primary/5 px-3 py-2 text-sm flex items-center gap-2">
				<Shield className="h-4 w-4 text-primary" />
				<span className="font-medium">{t("roles_full_access")}</span>
				<code className="text-[10px] text-muted-foreground ml-auto">*:*</code>
			</div>
		);
	}

	const grouped = new Map<string, string[]>();
	for (const p of permissions) {
		const existing = grouped.get(p.resource) ?? [];
		existing.push(p.action);
		grouped.set(p.resource, existing);
	}

	return (
		<div className="space-y-1">
			{Array.from(grouped.entries()).map(([resource, actions]) => (
				<div key={resource} className="rounded-lg border px-3 py-2">
					<div className="flex items-center gap-2 mb-1">
						<span className="text-sm font-medium">
							{getResourceLabels()[resource] ?? resource}
						</span>
						<code className="text-[10px] text-muted-foreground">
							{resource}
						</code>
					</div>
					<div className="flex flex-wrap gap-1.5 ml-0.5">
						{actions.map((action) => (
							<span
								key={action}
								className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-xs text-primary"
							>
								<Check className="h-3 w-3" />
								{ACTION_LABELS[action] ?? action}
							</span>
						))}
					</div>
				</div>
			))}
		</div>
	);
}

function RoleDetailDialog({
	role,
	onClose,
}: {
	role: RoleRow | null;
	onClose: () => void;
}) {
	const { t } = useTranslation("settings");

	if (!role) return null;

	return (
		<Dialog
			open={!!role}
			onOpenChange={(open) => {
				if (!open) onClose();
			}}
		>
			<DialogContent className="max-w-lg max-h-[85vh] flex flex-col gap-0 p-0">
				<DialogHeader className="px-6 pt-6 pb-2">
					<div className="flex items-center gap-2">
						<Shield className="h-5 w-5 text-muted-foreground" />
						<div>
							<DialogTitle>{role.displayName || role.name}</DialogTitle>
							<DialogDescription>
								{role.description || t("roles_no_description")}
							</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				<div className="px-6 pb-6 overflow-y-auto flex-1 min-h-0">
					<Tabs defaultValue="permissions">
						<TabsList>
							<TabsTrigger value="permissions">
								{t("roles_tab_permissions", { count: role.permissions.length })}
							</TabsTrigger>
							<TabsTrigger value="info">{t("roles_tab_info")}</TabsTrigger>
						</TabsList>

						<TabsContent value="permissions" className="mt-4">
							{role.permissions.length === 0 ? (
								<p className="text-sm text-muted-foreground py-4 text-center">
									{t("roles_no_permissions")}
								</p>
							) : (
								<PermissionGrid permissions={role.permissions} />
							)}
						</TabsContent>

						<TabsContent value="info" className="mt-4">
							<div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
								<span className="text-muted-foreground">ID</span>
								<code className="text-xs">{role.id}</code>
								<span className="text-muted-foreground">
									{t("roles_col_identifier")}
								</span>
								<code className="text-xs">{role.name}</code>
								<span className="text-muted-foreground">
									{t("roles_col_type")}
								</span>
								<Badge variant={role.isSystem ? "outline" : "secondary"}>
									{role.isSystem
										? t("roles_type_system")
										: t("roles_type_custom")}
								</Badge>
							</div>
						</TabsContent>
					</Tabs>
				</div>
			</DialogContent>
		</Dialog>
	);
}
